import React from 'react';
import { Box, Chip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { ByzMuiCheckBox } from 'src/components/core-components/mui/ByzMuiCheckBox';

export interface ProcessDataRow {
  id: number | string;
  date?: string;
  type?: string;
  time: string;
  action: string;
  isHeader?: boolean;
  stateId?: number;
  originalTime?: string;
}

export interface FlowStep {
  id: number;
  name: string;
  completed: boolean;
  active: boolean;
}

export const formatTime = (dateTime: string) => {
  if (!dateTime) return '-';
  try {
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return '-';
  }
};

export const getStatusStepNumber = (statusId: number) => {
  switch (statusId) {
    case 1650: return 1;
    case 1660: return 2;
    case 1665: return 3;
    case 1670: return 4;
    case 1675: return 5;
    case 1677: return 6;
    case 1680: return 7;
    case 1685: return 8;
    case 1687:
    case 1700:
    case 1705:
    case 1708:
    case 1690:
    case 1695:
      return 9;
    case 1715:
      return 1;
    default:
      return 0;
  }
};

export const getStatusColor = (status?: string) => {
  switch (status?.toLowerCase()) {
    case 'meliyathane tarafÄ±ndan Ã§aÄŸrÄ±ldÄ±':
      return 'info';
    case 'servise kabul edildi':
      return 'success';
    case 'ameliyathaneye gÃ¶nderildi':
      return 'error';
    case 'ameliyat baÅŸladÄ±':
      return 'primary';
    case 'ameliyat bitti':
      return 'secondary';
    default:
      return 'default';
  }
};

export const getOperationStatusText = (statusId: number) => {
  switch (statusId) {
    case 0: return 'Ä°PTAL';
    case 1: return 'PLANLANDI';
    case 2: return 'ONAYLANDI';
    case 3: return 'YAPILDI';
    case 4: return 'ERTELENDI';
    default: return 'BÄ°LÄ°NMEYEN';
  }
};

export const getOperationStatusColor = (statusId: number) => {
  switch (statusId) {
    case 0: return 'error';
    case 1: return 'warning';
    case 2: return 'info';
    case 3: return 'success';
    case 4: return 'default';
    default: return 'default';
  }
};

export const groupProcessHistoryByDate = (processData: any[]): ProcessDataRow[] => {
  const grouped: ProcessDataRow[] = [];
  const dateGroups: Record<string, ProcessDataRow[]> = {};

  processData.forEach((item) => {
    const rawTime = item.Ptime || item.ptime;
    const date = new Date(rawTime);
    const dateKey = date.toLocaleDateString('tr-TR');
    const timeKey = date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    if (!dateGroups[dateKey]) {
      dateGroups[dateKey] = [];
    }

    dateGroups[dateKey].push({
      id: item.id,
      date: dateKey,
      type: 'process',
      time: timeKey,
      action: item.StateName || item.statename,
      isHeader: false,
      stateId: item.StateId || item.stateid || item.psid || item.PsId,
      originalTime: rawTime,
    });
  });

  Object.keys(dateGroups).sort().reverse().forEach((dateKey) => {
    grouped.push({
      id: `date-${dateKey}`,
      date: dateKey,
      type: 'date-header',
      time: dateKey,
      action: dateKey,
      isHeader: true,
    });

    const sortedProcesses = dateGroups[dateKey].sort(
      (a, b) => new Date(b.originalTime || 0).getTime() - new Date(a.originalTime || 0).getTime()
    );

    grouped.push(...sortedProcesses);
  });

  return grouped;
};

export const createProcessSteps = (t: (key: string) => string, surgeryFlowState: number): FlowStep[] => [
  { id: 1650, name: t('surgery.flow.callPatient').replace(' ', '\n'), completed: surgeryFlowState > 1650, active: surgeryFlowState === 1650 },
  { id: 1665, name: t('surgery.flow.acceptedToOperatingRoom').replace(' ', '\n'), completed: surgeryFlowState > 1665, active: surgeryFlowState === 1665 },
  { id: 1670, name: t('surgery.flow.identityVerified').replace(' ', '\n'), completed: surgeryFlowState > 1670, active: surgeryFlowState === 1670 },
  { id: 1675, name: t('surgery.flow.onOperatingTable').replace(' ', '\n'), completed: surgeryFlowState > 1675, active: surgeryFlowState === 1675 },
  { id: 1677, name: t('surgery.flow.anesthesiaStarted').replace(' ', '\n'), completed: surgeryFlowState > 1677, active: surgeryFlowState === 1677 },
  { id: 1680, name: t('surgery.flow.surgeryStarted').replace(' ', '\n'), completed: surgeryFlowState > 1680, active: surgeryFlowState === 1680 },
  { id: 1685, name: t('surgery.flow.surgeryEnded').replace(' ', '\n'), completed: surgeryFlowState > 1685, active: surgeryFlowState === 1685 },
  { id: 1687, name: t('surgery.flow.anesthesiaEnded').replace(' ', '\n'), completed: surgeryFlowState > 1687, active: surgeryFlowState === 1687 },
];

export const createPostSurgerySteps = (t: (key: string) => string, surgeryFlowState: number): FlowStep[] => [
  { id: 1700, name: t('surgery.flow.inRecoveryRoom').replace(' ', '\n'), completed: surgeryFlowState > 1700, active: surgeryFlowState === 1700 },
  { id: 1705, name: t('surgery.flow.sendToWard').replace(' ', '\n'), completed: surgeryFlowState > 1705, active: surgeryFlowState === 1705 },
  { id: 1708, name: t('surgery.flow.sendToIntensiveCare').replace(' ', '\n'), completed: surgeryFlowState > 1708, active: surgeryFlowState === 1708 },
];

export const getStatusNameById = (statusId: number, processSteps: FlowStep[], postSurgerySteps: FlowStep[]) => {
  const processStep = processSteps.find((step) => step.id === statusId);
  if (processStep) return processStep.name;

  const postSurgeryStep = postSurgerySteps.find((step) => step.id === statusId);
  if (postSurgeryStep) return postSurgeryStep.name;

  if (statusId === 1690) return 'Ertelendi';
  if (statusId === 1695) return 'Ä°ptal';
  if (statusId === 1715) return 'Hasta YatÄ±ÅŸ Birimine GÃ¶nderildi';

  return 'Bilinmeyen Durum';
};

const renderTextCell = (value: unknown): string =>
  value !== null && value !== undefined && value !== '' ? String(value) : '-';

export const createProcessHistoryColumns = (t: (key: string) => string): GridColDef[] => [
  {
    field: 'time',
    headerName: t('surgery.tables.columns.time'),
    width: 80,
    minWidth: 70,
    renderCell: (params: any) => (params.row.isHeader ? null : params.row.time || '-'),
  },
  {
    field: 'action',
    headerName: t('surgery.tables.columns.action'),
    width: 200,
    minWidth: 150,
    renderCell: (params: any) => {
      if (params.row.isHeader) {
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              color: '#333',
              fontWeight: '600',
              fontSize: '0.85rem',
              px: 2,
              gridColumn: '1 / -1',
            }}
          >
            {params.row.action}
          </Box>
        );
      }
      return params.row.action || '-';
    },
  },
];

export const createOperationsColumns = (t: (key: string) => string): GridColDef[] => [
  {
    field: 'proccode',
    headerName: t('surgery.tables.columns.procedureCode'),
    width: 100,
    minWidth: 80,
    renderCell: (params: any) => renderTextCell(params.row?.proccode),
  },
  {
    field: 'procname',
    headerName: t('surgery.tables.columns.procedureName'),
    width: 200,
    minWidth: 150,
    renderCell: (params: any) => renderTextCell(params.row?.procname),
  },
  {
    field: 'procstateid',
    headerName: t('surgery.tables.columns.surgeryStatus'),
    width: 120,
    minWidth: 100,
    renderCell: (params: any) => (
      <Chip
        label={getOperationStatusText(params.row?.procstateid)}
        color={getOperationStatusColor(params.row?.procstateid)}
        size="small"
      />
    ),
  },
  {
    field: 'osname',
    headerName: t('surgery.tables.columns.procedureDirection'),
    width: 100,
    minWidth: 80,
    renderCell: (params: any) => renderTextCell(params.row?.osname),
  },
];

export const createSurgeryColumns = (t: (key: string) => string): GridColDef[] => [
  {
    field: 'offname',
    headerName: t('surgery.tables.columns.office'),
    width: 120,
    minWidth: 100,
    renderCell: (params: any) => renderTextCell(params.row?.offname),
  },
  {
    field: 'processstatusname2',
    headerName: t('surgery.tables.columns.status'),
    width: 80,
    minWidth: 70,
    renderCell: (params: any) => (
      <Chip label={params.value} color={getStatusColor(params.value)} size="small" />
    ),
  },
  {
    field: 'upn',
    headerName: t('surgery.tables.columns.fileNo'),
    width: 80,
    minWidth: 70,
    renderCell: (params: any) => renderTextCell(params.row?.upn),
  },
  {
    field: 'showMe',
    headerName: t('surgery.tables.columns.showMe'),
    width: 120,
    minWidth: 100,
    renderCell: () => <ByzMuiCheckBox checked={false} size="small" disabled />,
  },
  {
    field: 'patientname',
    headerName: t('surgery.tables.columns.firstName'),
    width: 80,
    minWidth: 60,
    renderCell: (params: any) => renderTextCell(params.row?.patientname),
  },
  {
    field: 'patientsurname',
    headerName: t('surgery.tables.columns.lastName'),
    width: 80,
    minWidth: 60,
    renderCell: (params: any) => renderTextCell(params.row?.patientsurname),
  },
  {
    field: 'plannedoperationtime',
    headerName: t('surgery.tables.columns.plannedSurgeryTime'),
    width: 120,
    minWidth: 100,
    renderCell: (params: any) => {
      const value = params.row?.plannedoperationtime;
      return value ? formatTime(value) : '-';
    },
  },
  {
    field: 'oproomname',
    headerName: t('surgery.tables.columns.operatingRoom'),
    width: 100,
    minWidth: 80,
    renderCell: (params: any) => renderTextCell(params.row?.oproomname),
  },
  {
    field: 'iscomplatepreop',
    headerName: t('surgery.tables.columns.preopReady'),
    width: 80,
    minWidth: 70,
    renderCell: (params: any) => (
      <Chip
        label={params.value === 1 ? t('surgery.tables.columns.completed') : t('surgery.tables.columns.waiting')}
        color={params.value === 1 ? 'success' : 'warning'}
        size="small"
      />
    ),
  },
  {
    field: 'chkident',
    headerName: t('surgery.tables.columns.identity'),
    width: 80,
    minWidth: 70,
    renderCell: (params: any) => (
      <Chip
        label={params.value === 1 ? t('surgery.tables.columns.verified') : t('surgery.tables.columns.waiting')}
        color={params.value === 1 ? 'success' : 'warning'}
        size="small"
      />
    ),
  },
  {
    field: 'opentryctime',
    headerName: t('surgery.tables.columns.admissionTime'),
    width: 90,
    minWidth: 80,
    renderCell: (params: any) => (params.row?.opentryctime ? formatTime(params.row.opentryctime) : '-'),
  },
  {
    field: 'opstarttime',
    headerName: t('surgery.tables.columns.surgeryStart'),
    width: 80,
    minWidth: 70,
    renderCell: (params: any) => (params.row?.opstarttime ? formatTime(params.row.opstarttime) : '-'),
  },
  {
    field: 'opendtime',
    headerName: t('surgery.tables.columns.surgeryEnd'),
    width: 80,
    minWidth: 70,
    renderCell: (params: any) => (params.row?.opendtime ? formatTime(params.row.opendtime) : '-'),
  },
  {
    field: 'anesstarttime',
    headerName: t('surgery.tables.columns.anesthesiaStart'),
    width: 100,
    minWidth: 90,
    renderCell: (params: any) => (params.row?.anesstarttime ? formatTime(params.row.anesstarttime) : '-'),
  },
  {
    field: 'anesendtime',
    headerName: t('surgery.tables.columns.anesthesiaEnd'),
    width: 100,
    minWidth: 90,
    renderCell: (params: any) => (params.row?.anesendtime ? formatTime(params.row.anesendtime) : '-'),
  },
  {
    field: 'estimatedwakeupduration',
    headerName: t('surgery.tables.columns.estimatedWakeupDuration'),
    width: 120,
    minWidth: 110,
    renderCell: (params: any) => params.row?.estimatedwakeupduration || '0',
  },
  {
    field: 'doctorname',
    headerName: t('surgery.tables.columns.doctor'),
    width: 120,
    minWidth: 100,
    renderCell: (params: any) => renderTextCell(params.row?.doctorname),
  },
  {
    field: 'pt2deptname',
    headerName: t('surgery.tables.columns.department'),
    width: 100,
    minWidth: 80,
    renderCell: (params: any) => renderTextCell(params.row?.pt2deptname),
  },
  {
    field: 'roomfloor',
    headerName: t('surgery.tables.columns.floorRoom'),
    width: 80,
    minWidth: 70,
    renderCell: (params: any) => renderTextCell(params.row?.roomfloor),
  },
  {
    field: 'isdischarge',
    headerName: t('surgery.tables.columns.discharged'),
    width: 100,
    minWidth: 90,
    renderCell: (params: any) => (
      <ByzMuiCheckBox checked={params.row?.isdischarge || false} size="small" disabled />
    ),
  },
  {
    field: 'specialforpatient',
    headerName: t('surgery.tables.columns.patientMaterials'),
    width: 150,
    minWidth: 140,
    renderCell: () => <ByzMuiCheckBox checked={false} size="small" disabled />,
  },
];
